import * as z from 'zod';
import { axiosInstance, toastOnError } from '@vl-core/api';
import { Options } from './helpers/type-helpers';
import { safeParse } from './helpers/safeParse';

export const path = '/meta/clinicians/APPUSER';

export const CliniciansMetaSchema = z.object({
  user_subtypes: z.array(
    z.object({
      user_subtype_code: z.string(),
      user_subtype_desc: z.string()
    })
  ),
  spec_cats: z.array(
    z.object({
      spec_cat_code: z.string(),
      spec_cat_name: z.string()
    })
  ),
  spec_areas: z.array(
    z.object({
      spec_cat_code: z.string(),
      spec_area_code: z.string(),
      spec_area_name: z.string(),
      image_url: z.string().nullable()
    })
  ),
  qualifications: z.array(
    z.object({
      qualification_code: z.string(),
      qualification_abbreviation: z.string(),
      qualification_desc: z.string()
    })
  ),
  rankings: z.array(
    z.object({
      client_id: z.number(),
      spec_cat_code: z.string(),
      user_subtype_code: z.string(),
      ranking_code: z.string(),
      ranking_desc: z.string(),
      min_rank_value: z.number(),
      max_rank_value: z.number()
    })
  ),
  special_interests: z.array(
    z.object({
      client_id: z.number(),
      interest_code: z.string(),
      interest_desc: z.string(),
      spec_cat_code: z.string()
    })
  ),
  max_interests: z.array(
    z.object({
      client_id: z.number(),
      spec_cat_code: z.string(),
      max_interests: z.number()
    })
  )
});

type CliniciansMeta = {
  user_subtypes: {
    user_subtype_code: string;
    user_subtype_desc: string;
  }[];
  spec_cats: {
    spec_cat_code: string;
    spec_cat_name: string;
  }[];
  spec_areas: {
    spec_cat_code: string;
    spec_area_code: string;
    spec_area_name: string;
    image_url: string | null;
  }[];
  qualifications: {
    qualification_code: string;
    qualification_abbreviation: string;
    qualification_desc: string;
  }[];
  rankings: {
    client_id: number;
    spec_cat_code: string;
    user_subtype_code: string;
    ranking_code: string;
    ranking_desc: string;
    min_rank_value: number;
    max_rank_value: number;
  }[];
  special_interests: {
    client_id: number;
    interest_code: string;
    interest_desc: string;
    spec_cat_code: string;
  }[];
  max_interests: {
    client_id: number;
    spec_cat_code: string;
    max_interests: number;
  }[];
};

export const ResponseSchema = z.object({
  meta: z.array(CliniciansMetaSchema)
});
export type Response = {
  meta: CliniciansMeta[];
};

export const getMetaClinicians = async (): Promise<CliniciansMeta[]> =>
  await axiosInstance.get(path).then((res) => safeParse(res.data, z.array(CliniciansMetaSchema)));

export const getMetaCliniciansQuery = (options?: Options) =>
  ({
    queryKey: [path],
    queryFn: () => getMetaClinicians(),
    select: (data: CliniciansMeta[]) => data[0],
    throwOnError: options?.skipToast ? false : toastOnError,
    staleTime: Infinity
  }) as const;

// Tests
let rs = {} as z.infer<typeof ResponseSchema>;
let rt = {} as Response;

rs = rt;
rt = rs;
